import {
  Button,
  ButtonGroup,
  HStack,
  IconButton,
  ListItem,
  Tooltip,
  UnorderedList,
  useDisclosure,
  useToast,
  VStack,
} from '@chakra-ui/react';
import DeleteSharpIcon from '@gamma/ui/icons/DeleteSharpIcon';
import { Alert, Modal } from '@gamma/ui/overlay';
import { useForm } from 'react-hook-form';
import { useApolloClient } from '@apollo/client';
import { i18n } from '../../../../shared/localization';
import { useRevokeAPIKey } from '../../queries';

const { revokeAPIKeyModal, revokeButtonTooltip } = i18n.pages.apiKeys;

interface RevokeAPIKeyProps {
  id: string;
  name: string;
}

export const RevokeAPIKey = ({ id, name }: RevokeAPIKeyProps) => {
  const { isOpen, onOpen, onClose } = useDisclosure();
  const { handleSubmit } = useForm();
  const showToastMessage = useToast();
  const client = useApolloClient();
  const [deleteAPIKeyDefinition, { loading }] = useRevokeAPIKey({
    onCompleted: () => {
      client.cache.evict({ fieldName: 'listAPIKeyDefinitions' });
      client.cache.gc();
      onClose();
      showToastMessage({
        status: 'success',
        description: revokeAPIKeyModal.toastSuccess,
        isClosable: true,
        position: 'bottom-right',
      });
    },
    onError: () => {
      showToastMessage({
        status: 'error',
        description: revokeAPIKeyModal.apiError,
        isClosable: true,
        position: 'bottom-right',
      });
    },
  });

  const handleRevokeAPIKey = handleSubmit(async () => {
    await deleteAPIKeyDefinition({ variables: { id } });
  });

  return (
    <>
      <Tooltip label={revokeButtonTooltip}>
        <IconButton
          size="box-md"
          aria-label="revoke-api-key"
          variant={'solid'}
          colorScheme="gray"
          onClick={(e) => {
            e.stopPropagation();
            onOpen();
          }}
          icon={<DeleteSharpIcon />}
        />
      </Tooltip>
      <Modal
        hideDivider
        isOpen={isOpen}
        onOpen={onOpen}
        onClose={onClose}
        closeOnEsc={!loading}
        closeOnOverlayClick={!loading}
        isCloseDisabled={loading}
        isCentered
        size="lg"
        title={i18n.formatString(revokeAPIKeyModal.title, name)}
        body={
          <VStack alignItems="left" gap={4}>
            <Alert status="warning" variant={'left-accent'}>
              <UnorderedList>
                {revokeAPIKeyModal.alertBodyItems.map((item, idx) => (
                  <ListItem key={idx}>{item}</ListItem>
                ))}
              </UnorderedList>
            </Alert>
          </VStack>
        }
        footer={
          <HStack>
            <ButtonGroup>
              <Button
                variant="solid"
                colorScheme="gray"
                onClick={onClose}
                isDisabled={loading}
              >
                {revokeAPIKeyModal.buttons.cancel}
              </Button>
              <Button
                variant="solid"
                colorScheme="red"
                onClick={handleRevokeAPIKey}
                isDisabled={loading}
                isLoading={loading}
              >
                {revokeAPIKeyModal.buttons.revokeKey}
              </Button>
            </ButtonGroup>
          </HStack>
        }
      />
    </>
  );
};
